import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/shared/action-registry';
import { DisputeStatus } from '../../../../../../../backend/src/types/types';
import { DetailsForm, detailsSchema } from '../../new/schema';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGenerateMatterIssues } from '@/api/mutations/useGenerateMatterIssues';
import { useUpdateAllDocsSynopsis } from '@/api/mutations/useUpdateAllDocsSynopsis';
import useUpdateMatter from '@/api/mutations/useUpdateMatter';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { DetailsInput } from '@/new/components/details-input';
import { GenerateDetailsButton } from '@/new/components/generate-details-button';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import { Button } from '@/new/components/shadcn/button';
import { Form } from '@/new/components/shadcn/form';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';

export function Issues() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { user } = useUserContext();
  const { data: matter } = useGetMatterV2(matterId);
  const isCreator = matter?.createdById === user?.id;
  const { data: allDocsSubset } = useGetSubset(matterId, matter?.allDocumentsCaseId ?? undefined);
  const { mutateAsync: updateMatter, isLoading: isUpdatingMatter } = useUpdateMatter(matterId);
  const { mutateAsync: updateAllDocsSynopsis, isLoading: isUpdatingSynopsis } = useUpdateAllDocsSynopsis(matterId);
  const { mutate: generateDetails, isLoading: isGeneratingDetails } = useGenerateMatterIssues({ matterId });
  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId));
  const hasProcessingRuns = processingState?.hasProcessingRuns ?? false;
  const hasDocuments = (allDocsSubset?.docCount ?? 0) > 0;

  const isSubmitting = isUpdatingMatter || isUpdatingSynopsis;
  const isFormDisabled = hasProcessingRuns || !hasDocuments;

  const form = useForm<DetailsForm>({
    resolver: zodResolver(detailsSchema),
    defaultValues: {
      issues: '',
      disputeType: '',
      userDefinedEntities: '',
      additionalContext: '',
      timePeriod: '',
      disputeStatus: undefined,
    },
  });

  useEffect(() => {
    if (matter) {
      form.reset({
        issues: matter.issues ?? '',
        disputeType: matter.disputeType ?? '',
        userDefinedEntities: matter.userDefinedEntities ?? '',
        additionalContext: matter.additionalContext ?? '',
        timePeriod: matter.timePeriod ?? '',
        disputeStatus: matter.disputeStatus ?? undefined,
      });
    }
  }, [matter]);

  const handleGenerateDetails = (file: File) => {
    trackCustomEvent(TrackingEventsV2.MatterGenerateDetails);
    generateDetails(
      { file },
      {
        onSuccess: (data) => {
          // if succeeded but all fields are null, show error
          if (Object.values(data).every((value) => value === null)) {
            toast.error(
              `Couldn't extract any matter details from the document. Please try again with a different file.`,
            );
            return;
          }

          if (data.caseIssues) form.setValue('issues', data.caseIssues);
          if (data.caseType) form.setValue('disputeType', data.caseType);
          if (data.caseEntities) form.setValue('userDefinedEntities', data.caseEntities);

          toast.success('Matter details extracted successfully');
        },
        onError: () => {
          toast.error('Failed to extract matter details. Please try again.');
        },
      },
    );
  };

  const onSubmit = async () => {
    const values = form.getValues();
    const issuesChanged = matter && values.issues !== matter.issues;

    try {
      await updateMatter({
        issues: values.issues,
        disputeType: values.disputeType,
        userDefinedEntities: values.userDefinedEntities,
        additionalContext: values.additionalContext,
        timePeriod: values.timePeriod,
        disputeStatus: values.disputeStatus as DisputeStatus,
      });

      if (issuesChanged && values.issues) {
        await updateAllDocsSynopsis({ issues: values.issues });
        toast.success('Matter issues updated. Synopsis update started.');
      } else {
        toast.success('Matter issues updated successfully.');
      }

      // Reset form state to mark as clean after successful save
      form.reset(values);
    } catch (error) {
      // Error toast is handled by mutation hooks
      console.error('Failed to update matter issues:', error);
    }
  };

  return (
    <PermissionGate permission={Permission.MatterEdit} orAllowed={isCreator} uiMode="disabled">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset disabled={isFormDisabled}>
            {matter && allDocsSubset && !hasDocuments && (
              <InfoCard className="mt-1 mb-4">Add documents before updating Matter issues.</InfoCard>
            )}

            <SettingsHeader
              title="Matter Issues"
              subheading={<p className="text-sm text-gray-500">View and change the issues for this matter.</p>}
              hasUnsavedChanges={form.formState.isDirty}
              action={
                <div className="flex items-center gap-2">
                  <GenerateDetailsButton
                    isGeneratingDetails={isGeneratingDetails}
                    onGenerateDetails={handleGenerateDetails}
                  />
                  <Button disabled={isSubmitting || !form.formState.isDirty || isFormDisabled} onClick={onSubmit}>
                    {isSubmitting ? (
                      <>
                        Submitting...
                        <Icon className="lucide--loader-circle h-3.5 w-3.5 animate-spin duration-700" />
                      </>
                    ) : (
                      <>
                        Submit
                        <Icon className="lucide--arrow-right h-3.5 w-3.5" />
                      </>
                    )}
                  </Button>
                </div>
              }
            />

            <DetailsInput form={form} isGeneratingDetails={isGeneratingDetails} disabled={isFormDisabled} />
          </fieldset>
        </form>
      </Form>
    </PermissionGate>
  );
}
